import React, { CSSProperties, FC, useEffect, useState } from 'react'
import { Alert, Button, Empty, Flex, Spin, Tag, Typography } from 'antd'
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons'
import { Well } from '@pushly/aqe/lib/components'
import { PQueryBuilder } from '@pushly/aqe/lib/components/pquery-builder'
import { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { CONTENT_DIRECTIVE_FIELDS } from './content-directive-fields'
import { getClassNames } from '../../_utils/classnames'
import { BRAND_VOICE_TIER_VISUAL } from './brand-voice-tier-visual'
import './brand-voice-decomposition.scss'

export type CompileState = 'idle' | 'compiling' | 'done' | 'error'

// A single compile can run 10+ seconds — these rotate under the spinner so the wait feels
// like progress rather than a stall. Each names something the compiler genuinely does
// (see directive-compiler.service.ts's SYSTEM_PROMPT), not generic filler.
const COMPILING_PHRASES = [
    'Reading through your Brand Voice…',
    'Splitting guarantees from best-effort guidance…',
    'Checking for emojis, exclamations, and banned words…',
    'Deciding what an AI judge should verify…',
    'Wrapping up the details…',
]
const COMPILING_PHRASE_INTERVAL_MS = 2800
const COMPILING_PHRASE_FADE_MS = 250

/**
 * Cycles through `phrases`, fading out briefly before swapping to the next one, only while
 * `active`. Resets to the first phrase whenever `active` turns true, so every new compile
 * starts the sequence fresh rather than resuming mid-cycle from the last one.
 */
function useCyclingPhrase(
    phrases: string[],
    intervalMs: number,
    fadeMs: number,
    active: boolean,
): { phrase: string; visible: boolean } {
    const [index, setIndex] = useState(0)
    const [visible, setVisible] = useState(true)

    useEffect(() => {
        if (!active) return

        setIndex(0)
        setVisible(true)

        let fadeTimeoutId: ReturnType<typeof setTimeout>
        const intervalId = setInterval(() => {
            setVisible(false)
            fadeTimeoutId = setTimeout(() => {
                setIndex((i) => (i + 1) % phrases.length)
                setVisible(true)
            }, fadeMs)
        }, intervalMs)
        return () => {
            clearInterval(intervalId)
            clearTimeout(fadeTimeoutId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `phrases` is a module-level constant
    }, [active, intervalMs, fadeMs])

    return { phrase: phrases[index], visible }
}

interface IBrandVoiceDecomposition {
    state: CompileState
    compiled: CompiledDirective | null
    onRetry: () => void
    /** The span key (see text-highlight.ts) currently linked to the textarea — hovering a
     * Checked/Needs-review row that has one highlights its source text; moving the caret into
     * a highlighted span in the textarea highlights the row back. Omit to render without the
     * highlight wiring (e.g. anywhere this component is used outside the editor drawer). */
    activeSpanKey?: string | null
    /** Keys (from findVerbatimSpans) that actually resolved to a span — a row only gets hover
     * wiring when its key is in this set, so a paraphrased item that couldn't be located just
     * renders as a normal, non-interactive row instead of linking to nothing. */
    matchedSpanKeys?: Set<string>
    onSpanHover?: (key: string | null) => void
    /** Clicking (not just hovering) a matched row also asks the textarea to scroll its source
     * text into view — a bigger, more deliberate ask than the hover-driven highlight, since a
     * click can happen from anywhere the eye lands without the textarea itself in view. Omit
     * alongside `onSpanHover` to render without highlight wiring at all. */
    onSpanClick?: (key: string) => void
}

/** True when the compiled spec is a non-empty PQuery condition group. */
function specHasConditions(spec: Record<string, unknown> | undefined): boolean {
    if (!spec) return false
    const group = (spec.and ?? spec.or) as unknown[] | undefined
    return Array.isArray(group) && group.length > 0
}

interface ISection {
    icon: React.ReactNode
    title: string
    caption: string
    count: number
    color: string
    /**
     * Full colored border + tinted background — reserved for a section that's genuinely an
     * exception worth flagging (Needs review). Guaranteed/Checked/Guidance are just three
     * flavors of "normal, expected content" competing for no one's urgent attention, so they
     * stay in the same neutral card chrome the rest of the product uses (see the Vespa
     * recommendation config's "Configuration Summary" card) — color there lives only on the
     * icon, not the whole box. Uniformly coloring all four would spend color-as-a-signal on
     * three sections that don't need it, diluting the one that does.
     */
    emphasized?: boolean
    children: React.ReactNode
}

// Uses the app's own `Well` (variant="outline", padding="small") rather than a hand-rolled
// bordered `div`, so this card tracks the platform's real theme tokens (including dark mode)
// instead of a set of manually-picked CSS var fallbacks that can silently drift from them.
export const InfoRow: FC<{
    children: React.ReactNode
    icon?: React.ReactNode
    style?: CSSProperties
    /** Outlines the row in `highlightColor` — set when this row's span key matches the
     * textarea<->rule highlight linking's active key. */
    highlighted?: boolean
    highlightColor?: string
    onMouseEnter?: () => void
    onMouseLeave?: () => void
    onClick?: () => void
}> = ({ children, icon, style, highlighted, highlightColor, onMouseEnter, onMouseLeave, onClick }) => (
    <Well
        style={{
            ...style,
            ...(highlighted && { boxShadow: `0 0 0 1px ${highlightColor ?? 'var(--ant-color-primary, #1677ff)'}` }),
            ...(onClick && { cursor: 'pointer' }),
        }}
        variant="outline"
        padding="small"
        shadow={false}
        hideHeader
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
    >
        <Flex align="flex-start" gap={8}>
            {icon && <span className={getClassNames('bvd-info-row-icon')}>{icon}</span>}
            <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        </Flex>
    </Well>
)

// Same rationale as InfoRow above: `Well` gives real theme-token-correct chrome instead of a
// hand-rolled border/background. `emphasized` (Needs review only, see the ISection docstring)
// tints Well's own border/background color via its `style` prop rather than introducing a
// second, one-off box implementation.
const Section: FC<ISection> = ({ icon, title, caption, count, color, emphasized, children }) => (
    <Well
        variant="outline"
        padding="small"
        titleSize="sm"
        shadow={false}
        className={getClassNames('bvd-section')}
        style={
            emphasized ? { borderColor: color, backgroundColor: `color-mix(in srgb, ${color}, white 95%)` } : undefined
        }
        title={
            <Flex vertical gap={2}>
                <Flex align="center" gap={8} className={getClassNames('bvd-section-header')}>
                    <span style={{ color, display: 'inline-flex' }}>{icon}</span>
                    <Typography.Text strong>{title}</Typography.Text>
                    <Tag bordered={false}>{count}</Tag>
                </Flex>
                <Typography.Text type="secondary" className={getClassNames('bvd-section-caption')}>
                    {caption}
                </Typography.Text>
            </Flex>
        }
    >
        <div className={getClassNames('bvd-section-body')}>{children}</div>
    </Well>
)

/**
 * Read-only decomposition of a compiled Brand Voice into its four tiers (NS-2668):
 *   - Guaranteed  -> `spec`       (deterministic PQuery, rendered via PQueryBuilder)
 *   - Checked     -> `assertions` (mandates verified by an AI judge at generation)
 *   - Guidance    -> `style`      (best-effort, applied verbatim)
 *   - Needs review-> `unmapped`   (stated but fits no rule/assertion)
 *
 * Per-rule editing / promotion / conflict resolution is intentionally out of scope here
 * (deferred to NS-2670); this view shows the author what their words compiled into.
 */
export const BrandVoiceDecomposition: FC<IBrandVoiceDecomposition> = ({
    state,
    compiled,
    onRetry,
    activeSpanKey = null,
    matchedSpanKeys,
    onSpanHover,
    onSpanClick,
}) => {
    const { phrase: compilingPhrase, visible: compilingPhraseVisible } = useCyclingPhrase(
        COMPILING_PHRASES,
        COMPILING_PHRASE_INTERVAL_MS,
        COMPILING_PHRASE_FADE_MS,
        state === 'compiling',
    )

    if (state === 'idle') {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Start describing your Brand Voice to see how it breaks down."
            />
        )
    }

    if (state === 'compiling') {
        return (
            <Flex vertical align="center" justify="center" gap={12} className={getClassNames('bvd-status')}>
                <Spin indicator={<LoadingOutlined spin />} />
                <Typography.Text type="secondary">Analyzing your Brand Voice…</Typography.Text>
                <Typography.Text
                    type="secondary"
                    className={getClassNames('bvd-compiling-phrase')}
                    // Opacity/transition stay inline (not in the stylesheet): the duration is
                    // tied to COMPILING_PHRASE_FADE_MS, the same constant driving the JS
                    // fade-swap timing above — a static CSS rule would be a second place that
                    // duration could silently drift out of sync with.
                    style={{
                        opacity: compilingPhraseVisible ? 1 : 0,
                        transition: `opacity ${COMPILING_PHRASE_FADE_MS}ms ease-in-out`,
                    }}
                >
                    {compilingPhrase}
                </Typography.Text>
            </Flex>
        )
    }

    if (state === 'error' || !compiled) {
        return (
            <Alert
                type="error"
                showIcon
                message="Couldn't analyze your Brand Voice"
                description="The directive compiler didn't respond. Your text is safe — nothing was changed. Until this succeeds, the AI falls back to your last saved rules."
                action={
                    <Button size="small" icon={<ReloadOutlined />} onClick={onRetry}>
                        Retry
                    </Button>
                }
            />
        )
    }

    const hasGuaranteed = specHasConditions(compiled.spec)
    const assertions = compiled.assertions ?? []
    const style = (compiled.style?.text ?? '').trim()
    const unmapped = compiled.unmapped ?? []

    return (
        <Flex vertical gap={16} className={getClassNames('brand-voice-decomposition')}>
            <Section
                icon={BRAND_VOICE_TIER_VISUAL.guaranteed.icon}
                title="Guaranteed"
                caption="Deterministically checked and enforced on every generation."
                count={hasGuaranteed ? (((compiled.spec.and ?? compiled.spec.or) as unknown[]) ?? []).length : 0}
                color={BRAND_VOICE_TIER_VISUAL.guaranteed.color}
            >
                {hasGuaranteed ? (
                    <PQueryBuilder
                        value={compiled.spec}
                        fields={CONTENT_DIRECTIVE_FIELDS}
                        disabled
                        // Overrides two tokens aqe re-declares on the builder's own root — see
                        // brand-voice-decomposition.scss's `.bvd-guaranteed-pqb` for why this
                        // has to be a class on the builder itself (via its own documented
                        // `className` prop) rather than a CSS variable set on a wrapping div.
                        className={getClassNames('bvd-guaranteed-pqb')}
                        // Required unconditionally outside a PQueryBuilderProvider — even in
                        // `disabled` mode, the library throws without it. A no-op is correct
                        // here, not a gap to fill in: this view is read-only by design, and
                        // per-rule editing is deferred to NS-2670 (see the docstring above).
                        onRuleCommit={() => {}}
                        // The section caption above already states "All of the following are
                        // true" (PQueryBuilder's own group header) — repeating "AND" beside
                        // every row below the first says the same thing again. Blank just the
                        // per-row connector; the group header (unaffected by this prop) stays.
                        labels={{ connectors: { and: '', or: '' } }}
                    />
                ) : (
                    <Typography.Text type="secondary">
                        No enforceable rules yet. Phrases like <Typography.Text code>never…</Typography.Text>,{' '}
                        <Typography.Text code>always…</Typography.Text>, or{' '}
                        <Typography.Text code>under 60 characters</Typography.Text> become guarantees.
                    </Typography.Text>
                )}
            </Section>

            <Section
                icon={BRAND_VOICE_TIER_VISUAL.checked.icon}
                title="Checked by AI review"
                caption="Mandates we can't measure directly — an AI judge verifies each generation against them, in your words."
                count={assertions.length}
                color={BRAND_VOICE_TIER_VISUAL.checked.color}
            >
                {assertions.length > 0 ? (
                    <ul className={getClassNames('bvd-assertions-list')}>
                        {assertions.map((a, i) => {
                            const key = `checked-${i}`
                            const matched = matchedSpanKeys?.has(key) ?? false
                            return (
                                <li key={i}>
                                    <InfoRow
                                        highlighted={activeSpanKey === key}
                                        highlightColor={BRAND_VOICE_TIER_VISUAL.checked.color}
                                        onMouseEnter={matched ? () => onSpanHover?.(key) : undefined}
                                        onMouseLeave={matched ? () => onSpanHover?.(null) : undefined}
                                        onClick={matched ? () => onSpanClick?.(key) : undefined}
                                    >
                                        <Typography.Text>{a.text}</Typography.Text>
                                    </InfoRow>
                                </li>
                            )
                        })}
                    </ul>
                ) : (
                    <Typography.Text type="secondary">No judged assertions.</Typography.Text>
                )}
            </Section>

            <Section
                icon={BRAND_VOICE_TIER_VISUAL.guidance.icon}
                title="Guidance"
                caption="Applied best-effort. We steer toward this, in your words — never reworded."
                count={style ? 1 : 0}
                color={BRAND_VOICE_TIER_VISUAL.guidance.color}
            >
                {style ? (
                    <InfoRow>
                        <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                            “{style}”
                        </Typography.Paragraph>
                    </InfoRow>
                ) : (
                    <Typography.Text type="secondary">No stylistic guidance yet.</Typography.Text>
                )}
            </Section>

            {unmapped.length > 0 && (
                <Section
                    icon={BRAND_VOICE_TIER_VISUAL.needsReview.icon}
                    title="Needs review"
                    caption="Stated clearly, but it fits no rule or assertion we can apply — surfaced so nothing is silently dropped."
                    count={unmapped.length}
                    color={BRAND_VOICE_TIER_VISUAL.needsReview.color}
                    emphasized
                >
                    <Flex vertical gap={8}>
                        {unmapped.map((u, i) => {
                            const key = `needsReview-${i}`
                            const matched = matchedSpanKeys?.has(key) ?? false
                            return (
                                <InfoRow
                                    key={i}
                                    icon={BRAND_VOICE_TIER_VISUAL.needsReview.icon}
                                    highlighted={activeSpanKey === key}
                                    highlightColor={BRAND_VOICE_TIER_VISUAL.needsReview.color}
                                    onMouseEnter={matched ? () => onSpanHover?.(key) : undefined}
                                    onMouseLeave={matched ? () => onSpanHover?.(null) : undefined}
                                    onClick={matched ? () => onSpanClick?.(key) : undefined}
                                >
                                    <Typography.Text>“{u.text}”</Typography.Text>
                                </InfoRow>
                            )
                        })}
                    </Flex>
                </Section>
            )}
        </Flex>
    )
}

/**
 * Compact, glanceable summary of a compiled directive — the counts a settings-page card
 * should lead with instead of the raw free-text body, which explains what was said but not
 * what actually got enforced. Reads persisted `compiled_rules` only; never triggers a compile.
 */
export const BrandVoiceSummaryChips: FC<{ compiled: CompiledDirective }> = ({ compiled }) => {
    const guaranteedCount = compiled.resolved?.length ?? 0
    const checkedCount = compiled.assertions?.length ?? 0
    const hasGuidance = !!(compiled.style?.text ?? '').trim()
    const needsReviewCount = compiled.unmapped?.length ?? 0

    const chips = [
        { key: 'guaranteed', ...BRAND_VOICE_TIER_VISUAL.guaranteed, text: `${guaranteedCount} Guaranteed` },
        { key: 'checked', ...BRAND_VOICE_TIER_VISUAL.checked, text: `${checkedCount} Checked` },
        hasGuidance ? { key: 'guidance', ...BRAND_VOICE_TIER_VISUAL.guidance, text: 'Guidance set' } : null,
        needsReviewCount > 0
            ? { key: 'needsReview', ...BRAND_VOICE_TIER_VISUAL.needsReview, text: `${needsReviewCount} needs review` }
            : null,
    ].filter((chip): chip is NonNullable<typeof chip> => chip !== null)

    return (
        <Flex gap={8} wrap="wrap">
            {chips.map((chip) => (
                <Tag key={chip.key} bordered={false} style={{ color: chip.color, background: 'rgba(0, 0, 0, 0.035)' }}>
                    <Flex align="center" gap={4} style={{ display: 'inline-flex' }}>
                        <span style={{ display: 'inline-flex', fontSize: 12 }}>{chip.icon}</span>
                        <span>{chip.text}</span>
                    </Flex>
                </Tag>
            ))}
        </Flex>
    )
}
